<script setup lang="ts">
import { TipCommand } from '@/ui/AppWysiwygEditor.vue';
import AppDictationButton from './AppDictationButton.vue';
import type { Editor } from '@tiptap/core';

interface Props {
  isBoldActive?: boolean;
  isItalicActive?: boolean;
  isParagraphActive?: boolean;
  isLevelOneHeadingActive?: boolean;
  isLevelTwoHeadingActive?: boolean;
  isLevelThreeHeadingActive?: boolean;
  isBulletListActive?: boolean;
  isOrderedListActive?: boolean;
  isTableActive?: boolean;
  isLinkActive?: boolean;
  enableTables?: boolean;
  enableLinks?: boolean;
  enableImages?: boolean;
  enableEmbeds?: boolean;
  editor?: Editor | null;
  enableDictation?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  enableDictation: false,
  editor: null,
});
const emit = defineEmits<{
  (e: 'toggle', param: TipCommand): string;
  (e: 'dictationError', message: string): void;
  (e: 'dictationStart'): void;
  (e: 'dictationEnd'): void;
}>();

const toggle = (param: TipCommand) => {
  emit('toggle', param);
};

const dictationButtonRef = ref<InstanceType<typeof AppDictationButton>>();
const isTableMenuOpen = ref(false);
const tableButtonRef = ref<HTMLButtonElement | null>(null);
const tableDropdownRef = ref<HTMLElement | null>(null);
const dropdownPosition = ref({ top: 0, left: 0 });

const updateDropdownPosition = () => {
  if (tableButtonRef.value) {
    const rect = tableButtonRef.value.getBoundingClientRect();
    dropdownPosition.value = {
      top: rect.bottom + window.scrollY + 4,
      left: rect.left + window.scrollX,
    };
  }
};

const toggleTableMenu = () => {
  if (!isTableMenuOpen.value) {
    updateDropdownPosition();
  }
  isTableMenuOpen.value = !isTableMenuOpen.value;
};

const handleTableAction = (action: TipCommand) => {
  emit('toggle', action);
  isTableMenuOpen.value = false;
};

// Close menu when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Node;
  const clickedButton = tableButtonRef.value?.contains(target);
  const clickedDropdown = tableDropdownRef.value?.contains(target);

  if (!clickedButton && !clickedDropdown) {
    isTableMenuOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});

const handleDictationError = (message: string) => {
  emit('dictationError', message);
};

const handleDictationStart = () => {
  emit('dictationStart');
};

const handleDictationEnd = () => {
  emit('dictationEnd');
};

defineExpose({
  dictationButtonRef,
});
</script>

<template>
  <div class="toolbar-wrapper">
    <span class="buttons-container">
      <button
        type="button"
        :class="{ 'is-active': props.isBoldActive }"
        class="icon-button"
        @mousedown.prevent
        @click="toggle('toggleBold')"
      >
        <UiIcon name="Bold" />
      </button>

      <button
        type="button"
        class="icon-button"
        :class="{ 'is-active': props.isItalicActive }"
        @mousedown.prevent
        @click="toggle('toggleItalic')"
      >
        <UiIcon name="Italic" />
      </button>

      <button
        type="button"
        class="icon-button"
        :class="{ 'is-active': props.isParagraphActive }"
        @mousedown.prevent
        @click="toggle('setParagraph')"
      >
        <UiIcon name="Pilcrow" />
      </button>

      <button
        type="button"
        class="icon-button"
        :class="{ 'is-active': props.isLevelOneHeadingActive }"
        @mousedown.prevent
        @click="toggle('toggleLevelOneHeading')"
      >
        <span>H1</span>
      </button>

      <button
        type="button"
        class="icon-button"
        :class="{ 'is-active': props.isLevelTwoHeadingActive }"
        @mousedown.prevent
        @click="toggle('toggleLevelTwoHeading')"
      >
        H2
      </button>

      <button
        type="button"
        class="icon-button"
        :class="{ 'is-active': props.isLevelThreeHeadingActive }"
        @mousedown.prevent
        @click="toggle('toggleLevelThreeHeading')"
      >
        H3
      </button>

      <button
        type="button"
        class="icon-button"
        :class="{ 'is-active': props.isBulletListActive }"
        @mousedown.prevent
        @click="toggle('toggleBulletList')"
      >
        <UiIcon name="List" />
      </button>

      <button
        type="button"
        class="icon-button"
        :class="{ 'is-active': props.isOrderedListActive }"
        @mousedown.prevent
        @click="toggle('toggleOrderedList')"
      >
        <UiIcon name="ListOrdered" />
      </button>

      <button
        type="button"
        class="icon-button"
        @mousedown.prevent
        @click="toggle('undo')"
      >
        <UiIcon name="Undo" />
      </button>

      <!-- Link button -->
      <button
        v-if="props.enableLinks"
        type="button"
        class="icon-button"
        :class="{ 'is-active': props.isLinkActive }"
        :title="$t('wysiwygEditor.link')"
        @mousedown.prevent
        @click="toggle('setLink')"
      >
        <UiIcon name="Link" />
      </button>

      <!-- Image button -->
      <button
        v-if="props.enableImages"
        type="button"
        class="icon-button"
        :title="$t('wysiwygEditor.image')"
        @mousedown.prevent
        @click="toggle('insertImage')"
      >
        <UiIcon name="Image" />
      </button>

      <!-- Video embed button -->
      <button
        v-if="props.enableEmbeds"
        type="button"
        class="icon-button"
        :title="$t('wysiwygEditor.embed')"
        @mousedown.prevent
        @click="toggle('insertEmbed')"
      >
        <UiIcon name="Video" />
      </button>

      <!-- Table button (only shown when tables are enabled) -->
      <button
        v-if="props.enableTables"
        ref="tableButtonRef"
        type="button"
        class="icon-button"
        :class="{ 'is-active': props.isTableActive }"
        @mousedown.prevent
        @click="toggleTableMenu"
      >
        <UiIcon name="Table" />
      </button>
    </span>

    <!-- Table dropdown menu (teleported to body to avoid overflow clipping) -->
    <Teleport v-if="props.enableTables" to="body">
      <div
        v-if="isTableMenuOpen"
        ref="tableDropdownRef"
        class="table-dropdown"
        :style="{
          position: 'absolute',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
        }"
      >
        <button
          type="button"
          class="dropdown-item"
          @mousedown.prevent
          @click="handleTableAction('insertTable')"
        >
          <UiIcon name="Plus" />
          <span>{{ $t('wysiwygEditor.table.insertTable') }}</span>
        </button>
        <div v-if="props.isTableActive" class="dropdown-divider" />
        <template v-if="props.isTableActive">
          <button
            type="button"
            class="dropdown-item"
            @mousedown.prevent
            @click="handleTableAction('addRowBefore')"
          >
            <UiIcon name="ArrowUp" />
            <span>{{ $t('wysiwygEditor.table.addRowBefore') }}</span>
          </button>
          <button
            type="button"
            class="dropdown-item"
            @mousedown.prevent
            @click="handleTableAction('addRowAfter')"
          >
            <UiIcon name="ArrowDown" />
            <span>{{ $t('wysiwygEditor.table.addRowAfter') }}</span>
          </button>
          <button
            type="button"
            class="dropdown-item dropdown-item--danger"
            @mousedown.prevent
            @click="handleTableAction('deleteRow')"
          >
            <UiIcon name="Trash2" />
            <span>{{ $t('wysiwygEditor.table.deleteRow') }}</span>
          </button>
          <div class="dropdown-divider" />
          <button
            type="button"
            class="dropdown-item"
            @mousedown.prevent
            @click="handleTableAction('addColumnBefore')"
          >
            <UiIcon name="ArrowLeft" />
            <span>{{ $t('wysiwygEditor.table.addColumnBefore') }}</span>
          </button>
          <button
            type="button"
            class="dropdown-item"
            @mousedown.prevent
            @click="handleTableAction('addColumnAfter')"
          >
            <UiIcon name="ArrowRight" />
            <span>{{ $t('wysiwygEditor.table.addColumnAfter') }}</span>
          </button>
          <button
            type="button"
            class="dropdown-item dropdown-item--danger"
            @mousedown.prevent
            @click="handleTableAction('deleteColumn')"
          >
            <UiIcon name="Trash2" />
            <span>{{ $t('wysiwygEditor.table.deleteColumn') }}</span>
          </button>
          <div class="dropdown-divider" />
          <button
            type="button"
            class="dropdown-item dropdown-item--danger"
            @mousedown.prevent
            @click="handleTableAction('deleteTable')"
          >
            <UiIcon name="Trash2" />
            <span>{{ $t('wysiwygEditor.table.deleteTable') }}</span>
          </button>
        </template>
      </div>
    </Teleport>

    <AppDictationButton
      v-if="props.enableDictation && props.editor"
      ref="dictationButtonRef"
      :editor="props.editor"
      class="dictation-button-aligned"
      @error="handleDictationError"
      @start="handleDictationStart"
      @end="handleDictationEnd"
    />
  </div>
</template>

<style lang="scss" scoped>
.toolbar-wrapper {
  display: flex;
  align-items: center;
  margin-bottom: var(--space-16);

  .buttons-container {
    display: flex;
    border-radius: var(--border-radius-6);
  }

  // Dictation button pushed to the right
  .dictation-button-aligned {
    margin-left: auto;
  }
}

button {
  background: white;
  border: 1px solid var(--color-gray-300);
  cursor: pointer;
}

button svg {
  width: 16px;
  height: 16px;
}

button span {
  font-size: var(--font-size-14);
  font-weight: var(--font-weight-medium);
  line-height: var(--line-height-ratio-100);
}

button.icon-button {
  padding: var(--space-8);
  color: var(--color-gray-500);
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: var(--color-gray-100, #f3f4f6);
  }

  &:first-of-type {
    border-top-left-radius: var(--border-radius-6);
    border-bottom-left-radius: var(--border-radius-6);
  }

  &:last-of-type {
    border-top-right-radius: var(--border-radius-6);
    border-bottom-right-radius: var(--border-radius-6);
  }

  // The selected-state text colour: primary-600 on this tint is 3.79:1, below
  // AA for the H1-H3 labels; primary-700 is 5.04:1.
  &.is-active {
    background-color: var(--color-primary-100, #dbeafe);
    color: var(--color-primary-700);
    border-color: var(--color-primary-200, #bfdbfe);

    svg {
      color: var(--color-primary-700);
    }

    span {
      color: var(--color-primary-700);
      font-weight: var(--font-weight-bold);
    }
  }
}

.buttons-container {
  position: relative;
  z-index: 0;
  display: flex;
  border-radius: var(--border-radius-6);
}
</style>

<!-- Non-scoped styles for teleported dropdown -->
<style lang="scss">
.table-dropdown {
  z-index: 10000;
  min-width: 180px;
  background: white;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--border-radius-6);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
  padding: var(--space-4) 0;

  .dropdown-item {
    display: flex;
    align-items: center;
    gap: var(--space-8);
    width: 100%;
    padding: var(--space-8) var(--space-12);
    font-size: var(--font-size-14);
    color: var(--color-gray-700);
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;

    &:hover {
      background-color: var(--color-gray-100);
    }

    svg {
      width: 16px;
      height: 16px;
      flex-shrink: 0;
    }

    &--danger {
      color: var(--color-error-600);

      &:hover {
        background-color: var(--color-error-50);
      }
    }
  }

  .dropdown-divider {
    height: 1px;
    margin: var(--space-4) 0;
    background-color: var(--color-gray-200);
  }
}
</style>
